import { Component, Inject, Input, OnChanges } from '@angular/core';
import { ContentService } from '@blk/cwp-ng-framework';
import { ChartType, ColumnChartName } from '@ddt-web/chart';
import { APPLICATION_NAME_TOKEN, ApplicationName, ColorsConfig } from '@ddt-web/config';
import { ConfigService, SharedConfigKey } from '@ddt-web/config-layer';
import { ComponentData, TabData } from '@ddt-web/data-service';
import { EsgTranslationUtil, ObjectUtil } from '@ddt-web/generic-utils';
import * as groupBy from 'lodash/groupBy';

import { extractAsOfDateFromMultiplePortfolios } from '../utils/esg-date.utils';

@Component({
	selector: 'esg-metrics',
	templateUrl: './esg-metrics.component.html',
	styleUrls: ['./esg-metrics.component.scss']
})
export class EsgMetricsComponent implements OnChanges {
	@Input() public data: ComponentData;
	@Input() public hideTabBarIfOneTab: boolean = false;
	@Input() public chartDisclosureDcrKey: string;
	@Input() public disclosureDcrKey: string;
	@Input() public titleDcrKey: string;
	@Input() public subTitleDcrKey: string;
	@Input() public tabsTitleDcrKey: string;
	@Input() public coverageDcrKey: string;
	@Input() public showSectionSeparator: boolean = false;
	@Input() public shouldDisplayRatingsScale: boolean = false;
	@Input() public esgChartAttributes: any = {};
	@Input() public tooltipDcr: string;
	@Input() public showLetterRatingTabs: string[] = []; // possible values in the array, ESG Score/Environmental Score/Social Score/Governance Score/
	@Input() public showCarbonScoreImpact: boolean = false;
	@Input() public wideTables: boolean = false; // true if we want all the tables to stretch to the page width if there's no ratings scale
	@Input() public shouldDisplayRankingsTable: boolean = false; // true, if we want to show the top and bottom ranked holdings component
	@Input() public shouldShowNoDataComponent: boolean = true;
	@Input() public showPortfolioCoverageAsTable: boolean = true; // when true, displays Portfolio Coverage data in tables, otherwise as text
	@Input() public showDisclosureAsOfDate: boolean = true;
	@Input() public showMethodologyCta: boolean = false;
	@Input() public showTopBottomHoldingsTable: boolean = false;

	public asOfDate: string;
	public chartType: ChartType = ChartType.Column;
	public chartName: ColumnChartName = ColumnChartName.NileRisk;
	public customChartAttributes: any[];
	public percentagesTab: TabData;
	public chartTabs: TabData[];
	public carbonScoreTab: TabData;
	public methodologyLink: string;
	public topHoldingTabs: TabData[];
	public bottomHoldingTabs: TabData[];

	public singlePortColumnProportions: number[];
	public multiPortColumnProportions: number[];
	public isTopBottomHoldingsTableEnabled: boolean;

	constructor(
		public configService: ConfigService,
		public contentService: ContentService,
		@Inject(APPLICATION_NAME_TOKEN) protected applicationName: ApplicationName
	) { }

	public ngOnChanges(): void {
		if (this.data.success) {
			// This is temporary and can be removed later
			this.isTopBottomHoldingsTableEnabled = this.contentService.getBoolean('ESG_TOP_BOTTOM_TABLE_ENABLED', true);
			const { asOfDateTab, percentagesTab, topHoldingTabs, bottomHoldingTabs, chartTabs } = groupBy(this.data.tabs,
				(tab) => {
					const id = tab?.config?.id;
					if (id === 'esg.asOfDate') { return 'asOfDateTab'; }
					if (id === 'esg.percentages') { return 'percentagesTab'; }
					if (id.includes('esg.top')) { return 'topHoldingTabs'; }
					if (id.includes('esg.bottom')) { return 'bottomHoldingTabs'; }
					return 'chartTabs';
				});
			this.asOfDate = extractAsOfDateFromMultiplePortfolios(asOfDateTab?.[0]);
			this.percentagesTab = percentagesTab?.[0];
			this.topHoldingTabs = topHoldingTabs;
			this.bottomHoldingTabs = bottomHoldingTabs;
			if (this.showCarbonScoreImpact) {
				this.carbonScoreTab = this.data.getTabById('esg.carbonIntensity');
			}
			this.singlePortColumnProportions = this.showCarbonScoreImpact ? [80, 20] : [50, 20, 15, 15];
			this.multiPortColumnProportions = this.showCarbonScoreImpact ? [70, 20, 10] : [40, 20, 10, 15, 15];

			this.chartTabs = chartTabs;
			this.customChartAttributes = this.chartTabs.map((tab, tabIndex) => {
				let customAttributes = ObjectUtil.extend({
					plotOptions: {
						series: {
							animation: false
						},
						column: {
							colorByPoint: true
						}
					},
					yAxis: {
						title: {
							enabled: true,
							text: tab?.tableData?.[0]?.headersY?.[0]?.rawValue
						}
					},
					seriesLabelsFormatter: '{y:0.2f}',
					hasDataLabels: true,
					translateEsgValue: this.shouldTranslateValue(tab),
					esgRatingsScaleMap: this.configService.get<{ [rating: string]: number }>(SharedConfigKey.EsgRatingsRangeScale, {}),
					chart: { height: 350 },
					colors: ColorsConfig.PORTFOLIO_COLORS
				}, this.esgChartAttributes);
				if (tab.config.id === 'overall.qualityScore') {
					customAttributes = ObjectUtil.extend(customAttributes, {
						useHTML: true,
						tooltip: {
							formatter: function (): string {
								const translatedLabel: string = EsgTranslationUtil.getEsgLetterRating(customAttributes.esgRatingsScaleMap, this.y);
								return `<div class="text-bold">${this.key}</div>` +
									`<div class="tooltip row text-12"><span class="col-16">${this.series.name}</span>` +
									`<span class="col-8 text-align-right">${translatedLabel}</span></div>`;
							}
						}
					});
				}
				return customAttributes;
			});
		}
		this.methodologyLink = this.contentService.get('ESG_METHODOLOGY_CTA_LINK', '');
	}

	private shouldTranslateValue(tab: TabData): boolean {
		const rating = tab?.tableData[0]?.headersY[0]?.rawValue;
		return this.showLetterRatingTabs.includes(rating?.toString());
	}
}
